import type {
  CreateMealInsightResponse,
  MealInsightHistoryResponse
} from "@9amhealth/openapi";
import {
  DateFormatter,
  getLocalTimeZone,
  parseDate,
  parseDateTime,
  toCalendarDate,
  today as todayInZone,
  toZoned,
  type CalendarDateTime,
  type ZonedDateTime
} from "@internationalized/date";
import { getSupportedUserLocale } from "src/lib/i18next";
import translate from "src/lib/translate";
import type { CalorieStat } from "src/state/MealsStatsCubit/MealsStatsCubit";

/**
 * Eating occasions the daily insight is gated behind (BACK-5890). The stats
 * endpoint still returns encouragement copy on a thin day — it only omits
 * `insights` when generation fails outright — so the gate has to be applied here
 * rather than inferred from the text being absent.
 */
export const DAILY_INSIGHT_MEAL_THRESHOLD = 2;

export type DailyInsightState = "intro" | "locked" | "unlocked";

export interface DailyInsightInput {
  /** Whether the member has ever logged a meal, on any day. */
  hasAnyMeals: boolean;
  /** Today's insight text from the stats endpoint; empty when none was returned. */
  insights: string;
  /** Meals logged today. */
  mealCount: number;
}

/**
 * Which of the three daily insight cards the Meals tab shows. Presence of
 * `insights` is not enough on its own: the stats endpoint writes encouragement
 * copy for a day with one meal or none, and omits the text only when generation
 * failed — so the meal count has to agree before the insight counts as unlocked.
 */
export const dailyInsightState = ({
  hasAnyMeals,
  insights,
  mealCount
}: DailyInsightInput): DailyInsightState => {
  if (!hasAnyMeals) {
    return "intro";
  }
  const unlocked =
    Boolean(insights) && mealCount >= DAILY_INSIGHT_MEAL_THRESHOLD;
  return unlocked ? "unlocked" : "locked";
};

/** Meals logged today, capped so the unlock hint can never read past its target. */
export const unlockProgress = (mealCount: number): number =>
  Math.min(Math.max(mealCount, 0), DAILY_INSIGHT_MEAL_THRESHOLD);

export const formatCalorieRange = (
  from?: number,
  to?: number
): string | undefined => {
  if (from && to) {
    return `${from}-${to} kcal`;
  }
  if (from) {
    return `${from}+ kcal`;
  }
  return undefined;
};

export const formatNutrition = (value?: number, unit = "g"): string => {
  return value ? `${Math.round(value)} ${unit}` : "--";
};

/**
 * "Today" / "Yesterday" / "Mon, Aug 24" for a calendar day, relative to the
 * viewer's current local day. The year is appended once the day falls outside
 * the current one, so an old entry can't read as this year's.
 */
export const mealDayLabel = (dateKey: string, today: string): string => {
  if (dateKey === today) {
    return translate("mealInsights.meals.today");
  }
  if (dateKey === parseDate(today).subtract({ days: 1 }).toString()) {
    return translate("mealInsights.meals.yesterday");
  }
  const date = parseDate(dateKey);
  const isCurrentYear = date.year === parseDate(today).year;
  return new DateFormatter(getSupportedUserLocale(), {
    weekday: "short",
    month: "short",
    day: "numeric",
    ...(isCurrentYear ? {} : { year: "numeric" })
  }).format(date.toDate(getLocalTimeZone()));
};

/**
 * The wall clock a meal was logged at, with its offset dropped.
 *
 * `takenAt` carries the offset it was recorded in ("2026-08-31T10:04:00+02:00"),
 * and that reading is the meal's own: something eaten at 10:04 in +02:00 still
 * reads 10:04 when it is opened from another timezone. Re-zoning it to the
 * viewer would rewrite both the time and, near midnight, the day it belongs to —
 * so the offset is deliberately discarded rather than converted.
 */
const mealWallClock = (takenAt: string): CalendarDateTime =>
  parseDateTime(takenAt.replace(/(?:Z|[+-]\d{2}:?\d{2})$/, ""));

/** The clock time a meal was logged at ("10:04 AM"). */
export const formatMealTime = (takenAt: string): string =>
  new DateFormatter(getSupportedUserLocale(), {
    hour: "numeric",
    minute: "2-digit"
  }).format(mealWallClock(takenAt).toDate(getLocalTimeZone()));

/** The calendar day a meal belongs to, as logged. */
export const mealDayKey = (takenAt: string): string =>
  toCalendarDate(mealWallClock(takenAt)).toString();

/**
 * Orders meals by the time they read as. Sorting on the raw instant would put a
 * meal logged in another offset out of sequence with the times shown next to it.
 */
export const mealSortKey = (takenAt: string): string =>
  mealWallClock(takenAt).toString();

/** The meal's logged reading, as the date pickers want it. */
export const mealTakenAtAsZoned = (takenAt: string): ZonedDateTime =>
  toZoned(mealWallClock(takenAt), getLocalTimeZone());

/**
 * The meal header's timestamp: the same day label the meal list uses, joined to
 * the clock time ("Today at 9:00 AM").
 */
export const formatMealTakenAt = (takenAt: string): string =>
  translate("mealInsights.takenAt.label", {
    day: mealDayLabel(
      mealDayKey(takenAt),
      todayInZone(getLocalTimeZone()).toString()
    ),
    time: formatMealTime(takenAt)
  });

/**
 * `ZonedDateTime.toString()` appends the IANA zone ("...+02:00[Europe/Vienna]"),
 * which the API's `date-time` fields reject — it stores the instant plus the
 * offset the meal was logged in, so only the bracketed zone is dropped.
 */
export const toOffsetDateTimeString = (value: ZonedDateTime): string =>
  value.toString().replace(/\[.*\]$/, "");

/**
 * How far past the calorie target the day's estimate has to land before the bar
 * flags it. Under this allowance the estimate's own error bars are wider than
 * the overage, so it is not worth calling out.
 */
export const CALORIE_OVERSHOOT_ALLOWANCE_KCAL = 100;

/**
 * True once today's calorie estimate is materially — not marginally — above the
 * member's target, which is when the bar grows its overshoot segment.
 */
export const isCalorieOvershoot = ({ total, target }: CalorieStat): boolean =>
  target > 0 && total > target + CALORIE_OVERSHOOT_ALLOWANCE_KCAL;

export const refinedHistoryEntry = (
  entry: MealInsightHistoryResponse,
  refined: CreateMealInsightResponse
): MealInsightHistoryResponse => {
  const { insights } = refined;
  const { from, to } = insights.caloriesRange ?? {};

  return {
    ...entry,
    takenAt: refined.takenAt,
    refinementCount: refined.refinementCount,
    mealIdentified: insights.mealIdentified ?? false,
    mealName: insights.mealName,
    insights: insights.insights,
    nutrients: {
      protein: insights.protein,
      fiber: insights.fiber,
      totalFats: insights.totalFats,
      totalCarbohydrates: insights.totalCarbohydrates,
      caloriesRange:
        from !== undefined && to !== undefined ? { from, to } : undefined
    }
  };
};
